import RichText from "@/components/RichText";
import { CONTAINER, PANEL_BG, SERIF } from "../tokens";

// Bandeau principal du handoff : accroche + deux boutons + chiffres de
// preuve à gauche, panneau « Suivi de projet » à droite.
//
// Le titre est en TROIS morceaux (`headline_before` / `headline_italic` /
// `headline_after`) parce que le handoff met un seul mot en serif italique
// vert au milieu de la phrase — un champ « titre + surlignage » comme
// ailleurs ne saurait pas rendre la ponctuation qui suit ce mot.
const DEFAULTS = {
  eyebrow: "",
  headline_before: "",
  headline_italic: "",
  headline_after: "",
  subheadline: "",
  cta_primary_label: "",
  cta_primary_url: "#tarifs",
  cta_secondary_label: "",
  cta_secondary_url: "#contact",
  panel_eyebrow: "",
  panel_status: "",
  panel_title: "",
  panel_price: "",
  panel_price_note: "",
  panel_progress: "0",
  panel_progress_left: "",
  panel_progress_right: "",
};

const pct = (v) => Math.max(0, Math.min(100, Number.parseFloat(v) || 0));

export default function HeroSection({ props = {}, blocks = [] }) {
  const t = { ...DEFAULTS, ...props };
  const of = (type) => (blocks || []).filter((b) => b.type === type && b.enabled !== false);
  const proofs = of("proof_item");
  const steps = of("panel_step");
  const pills = of("panel_pill");
  // Panneau masquable : le vider est le seul moyen, dans l'éditeur, de
  // retrouver un bandeau sur une seule colonne.
  const hasPanel = Boolean(t.panel_title || steps.length || t.panel_price);

  return (
    <section
      className={`${CONTAINER} pt-[84px] pb-[70px] grid gap-[60px] items-center ${hasPanel ? "md:grid-cols-2" : ""}`}
    >
      <div>
        {t.eyebrow && (
          <div className="inline-flex items-center gap-[9px] border-control border-ink/[0.16] rounded-full py-[7px] px-[14px] font-display text-[12.5px] tracking-[0.06em] uppercase text-ink/70">
            <span className="h-1.5 w-1.5 rounded-full bg-blush shrink-0" />
            <RichText html={t.eyebrow} />
          </div>
        )}

        <h1 className="font-display font-semibold text-[clamp(37px,5.2vw,66px)] leading-[1.02] tracking-[-0.035em] mt-[26px] text-ink [text-wrap:pretty]">
          <RichText html={t.headline_before} />
          {t.headline_italic && (
            <>
              {" "}
              <RichText
                className="italic font-normal text-gold"
                style={{ fontFamily: SERIF }}
                html={t.headline_italic}
              />
            </>
          )}
          {t.headline_after}
        </h1>

        <RichText
          as="p"
          preserveParagraphs
          className="text-[18px] leading-[1.6] text-ink/[0.66] max-w-[520px] mt-6 [text-wrap:pretty]"
          html={t.subheadline}
        />

        <div className="flex flex-wrap gap-3 mt-8">
          {t.cta_primary_label && (
            <a
              href={t.cta_primary_url || "#tarifs"}
              className="font-display font-medium text-[15px] bg-blush text-ink py-[14px] px-6 rounded-btn hover:opacity-90 transition-opacity"
            >
              {t.cta_primary_label}
            </a>
          )}
          {t.cta_secondary_label && (
            <a
              href={t.cta_secondary_url || "#contact"}
              className="font-display font-medium text-[15px] text-ink border-control border-ink/[0.22] py-[14px] px-6 rounded-btn hover:border-ink/50 transition-colors"
            >
              {t.cta_secondary_label}
            </a>
          )}
        </div>

        {proofs.length > 0 && (
          <div className="flex flex-wrap gap-[26px] mt-11 pt-[26px] border-t border-ink/[0.09]">
            {proofs.map((b) => (
              <div key={b.id} data-block-key={b.id}>
                <div className="font-display text-[25px] font-semibold tracking-[-0.02em] text-ink">
                  {b.props?.value}
                </div>
                <div className="text-[13px] text-ink/[0.55] mt-0.5">{b.props?.caption}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {hasPanel && (
        <div
          className="text-cream border-control border-ink/[0.14] rounded-[18px] p-[26px] shadow-[0_26px_60px_-32px_rgba(0,0,0,0.6)]"
          style={PANEL_BG}
        >
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div className="font-display text-[12px] tracking-[0.1em] uppercase text-cream/[0.55]">
              {t.panel_eyebrow}
            </div>
            {t.panel_status && (
              <div className="flex items-center gap-2 text-[13px] text-cream/[0.72]">
                <span className="h-[7px] w-[7px] rounded-full bg-blush" />
                {t.panel_status}
              </div>
            )}
          </div>

          <RichText
            as="div"
            className="font-display font-semibold text-[23px] tracking-[-0.02em] mt-3.5"
            html={t.panel_title}
          />

          {t.panel_price && (
            <div className="flex items-baseline gap-2.5 mt-4 flex-wrap">
              <span className="text-[44px] leading-none" style={{ fontFamily: SERIF }}>
                {t.panel_price}
              </span>
              <span className="text-[13.5px] text-cream/[0.6]">{t.panel_price_note}</span>
            </div>
          )}

          <div className="h-1.5 rounded-full bg-cream/[0.14] mt-6 overflow-hidden">
            <div className="h-full bg-blush" style={{ width: `${pct(t.panel_progress)}%` }} />
          </div>
          <div className="flex justify-between text-[12.5px] text-cream/[0.55] mt-[9px]">
            <span>{t.panel_progress_left}</span>
            <span>{t.panel_progress_right}</span>
          </div>

          {steps.length > 0 && (
            <div className="flex flex-col gap-[13px] mt-[26px] pt-[22px] border-t border-cream/[0.14] text-[14.5px]">
              {steps.map((b) => {
                const done = String(b.props?.done) === "true";
                return (
                  <div
                    key={b.id}
                    data-block-key={b.id}
                    className={`flex items-center gap-[11px] ${done ? "" : "text-cream/[0.55]"}`}
                  >
                    {/* Glyphes textuels, comme le handoff — aucune icône. */}
                    <span className={done ? "text-blush" : ""}>{done ? "✓" : "○"}</span>
                    <span className="flex-1">{b.props?.label}</span>
                    <span className={`text-[13px] ${done ? "text-cream/[0.5]" : ""}`}>
                      {b.props?.date_label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {pills.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {pills.map((b) => (
                <span
                  key={b.id}
                  data-block-key={b.id}
                  className="border-control border-cream/[0.18] rounded-full py-[7px] px-[13px] text-[13px] text-cream/[0.78]"
                >
                  {b.props?.label}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
